/**
 * Detect whether a local video file has an audio track.
 * Returns false only when confidently silent; unknown → true (do not block).
 *
 * @param {File|Blob} file
 * @returns {Promise<boolean>}
 */
export async function videoFileHasAudioTrack(file) {
  if (!file || typeof document === 'undefined') return true

  try {
    const probe = await probeIsoBmffHasAudio(file)
    if (probe === true || probe === false) return probe
  } catch {
    /* fall through */
  }

  try {
    return await detectViaVideoElement(file)
  } catch {
    return true
  }
}

/**
 * ISO BMFF (MP4 / MOV / M4V): look for hdlr handler_type `soun` / `vide`.
 * @param {File|Blob} file
 * @returns {Promise<boolean|null>}
 */
async function probeIsoBmffHasAudio(file) {
  const name = String(file.name || '').toLowerCase()
  const type = String(file.type || '').toLowerCase()
  const looksIso =
    type.includes('mp4') ||
    type.includes('quicktime') ||
    type.includes('m4v') ||
    /\.(mp4|mov|m4v)$/i.test(name)
  if (!looksIso && type && !type.startsWith('video/')) return null

  const maxBytes = Math.min(file.size || 0, 5 * 1024 * 1024)
  if (maxBytes < 32) return null
  const buf = await file.slice(0, maxBytes).arrayBuffer()
  const u8 = new Uint8Array(buf)

  let hasSoun = false
  let hasVide = false
  for (let i = 0; i < u8.length - 20; i += 1) {
    // box type "hdlr"
    if (
      u8[i] !== 0x68 ||
      u8[i + 1] !== 0x64 ||
      u8[i + 2] !== 0x6c ||
      u8[i + 3] !== 0x72
    ) {
      continue
    }
    // FullBox: version/flags (4) + pre_defined (4) + handler_type (4)
    const t = i + 16
    if (t + 4 > u8.length) continue
    const handler = String.fromCharCode(u8[t], u8[t + 1], u8[t + 2], u8[t + 3])
    if (handler === 'soun') hasSoun = true
    if (handler === 'vide') hasVide = true
  }

  if (hasSoun) return true
  if (hasVide && !hasSoun) return false
  return null
}

/**
 * @param {File|Blob} file
 * @returns {Promise<boolean>}
 */
function detectViaVideoElement(file) {
  return new Promise((resolve) => {
    const video = document.createElement('video')
    video.preload = 'auto'
    video.muted = true
    video.playsInline = true
    const objectUrl = URL.createObjectURL(file)
    let settled = false

    const finish = (value) => {
      if (settled) return
      settled = true
      cleanup()
      resolve(value)
    }

    const cleanup = () => {
      try {
        video.pause()
      } catch {
        /* ignore */
      }
      video.removeAttribute('src')
      video.load()
      URL.revokeObjectURL(objectUrl)
    }

    const readHasAudio = () => {
      if (typeof video.mozHasAudio === 'boolean') return video.mozHasAudio
      try {
        if (video.audioTracks && typeof video.audioTracks.length === 'number') {
          return video.audioTracks.length > 0
        }
      } catch {
        /* ignore */
      }
      try {
        const capture =
          typeof video.captureStream === 'function'
            ? video.captureStream()
            : typeof video.mozCaptureStream === 'function'
              ? video.mozCaptureStream()
              : null
        if (capture) {
          const count = capture.getAudioTracks().length
          capture.getTracks().forEach((track) => {
            try {
              track.stop()
            } catch {
              /* ignore */
            }
          })
          return count > 0
        }
      } catch {
        /* ignore */
      }
      const decoded = Number(video.webkitAudioDecodedByteCount)
      if (Number.isFinite(decoded) && decoded > 0) return true
      return null
    }

    const timer = window.setTimeout(() => finish(true), 4000)

    video.onerror = () => {
      window.clearTimeout(timer)
      finish(true)
    }

    video.onloadeddata = () => {
      const immediate = readHasAudio()
      if (immediate === true || immediate === false) {
        window.clearTimeout(timer)
        finish(immediate)
        return
      }

      const playResult = video.play()
      const afterPlay = () => {
        window.setTimeout(() => {
          const again = readHasAudio()
          window.clearTimeout(timer)
          finish(again === false ? false : true)
        }, 180)
      }
      if (playResult && typeof playResult.then === 'function') {
        playResult.then(afterPlay).catch(() => {
          window.clearTimeout(timer)
          finish(true)
        })
      } else {
        afterPlay()
      }
    }

    video.src = objectUrl
  })
}
